import type { ApprovalRequest } from '@/lib/types';
import { formatPKR } from '../format';
import { BucketShell } from './_BucketShell';
import { readStructuredFields } from './_payload';

export interface ServicePrepViewProps {
  req: ApprovalRequest;
}

export function ServicePrepView({ req }: ServicePrepViewProps) {
  const sf = readStructuredFields(req);
  const preflight = (sf['service_preflight'] ?? {}) as Record<string, unknown>;
  const lines = Array.isArray(preflight['service_lines'])
    ? (preflight['service_lines'] as Record<string, unknown>[])
    : [];
  const taxChecked = readTaxStatus(preflight['stax_checked']);

  return (
    <BucketShell req={req}>
      <section className="rounded-lg border border-purple-300 bg-purple-50 p-16">
        <p className="text-xs font-semibold uppercase tracking-wide text-purple-700">
          Service invoice — validate and post
        </p>
        <p className="mt-4 text-sm text-neutral-700">
          Open the service invoice modal from the footer to review the pre-filled
          values, then use Validate and post to submit the invoice to Oracle.
        </p>
        {Object.keys(preflight).length > 0 && (
          <div className="mt-12 rounded-md border border-purple-200 bg-neutral-0 p-12">
            <div className="flex flex-wrap items-start justify-between gap-12">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                  Service preflight
                </p>
                <p className="mt-4 text-sm text-neutral-700">
                  Invoice-level tax {taxChecked.toLowerCase()}.
                </p>
              </div>
              <div className="text-right text-sm font-medium text-neutral-900">
                <p>{formatWholePKR(preflight['gross_amount'])}</p>
                <p className="text-xs font-normal uppercase tracking-wide text-neutral-500">
                  Gross amount
                </p>
              </div>
            </div>
            <div className="mt-12 grid gap-12 tablet:grid-cols-3">
              <Readout label="Service book" value={readText(preflight['service_book_name'])} />
              <Readout
                label="Supplier invoice ref"
                value={readText(preflight['supplier_invoice_ref'])}
              />
              <Readout label="Invoice date" value={readText(preflight['invoice_date'])} />
              <Readout label="Taxable amount" value={formatWholePKR(preflight['taxable_amount'])} />
              <Readout
                label="Non-taxable amount"
                value={formatWholePKR(preflight['non_taxable_amount'])}
              />
              <Readout label="Tax amount" value={formatWholePKR(preflight['tax_amount'])} />
              <Readout label="Payment term" value={readPaymentTerm(preflight)} />
            </div>
            {lines.length > 0 && (
              <div className="mt-16 overflow-x-auto rounded-md border border-neutral-200">
                <table className="min-w-full divide-y divide-neutral-200 text-sm">
                  <thead className="bg-neutral-50 text-xs uppercase tracking-wide text-neutral-500">
                    <tr>
                      <th className="px-12 py-8 text-left font-semibold">Description</th>
                      <th className="px-12 py-8 text-left font-semibold">Account code</th>
                      <th className="px-12 py-8 text-right font-semibold">Amount</th>
                      <th className="px-12 py-8 text-right font-semibold">Taxable</th>
                      <th className="px-12 py-8 text-right font-semibold">Tax</th>
                      <th className="px-12 py-8 text-left font-semibold">Tax status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100 bg-neutral-0">
                    {lines.map((line, index) => (
                      <tr key={`${readText(line['description'])}-${index}`}>
                        <td className="max-w-[320px] px-12 py-10 text-neutral-900">
                          {readText(line['description'])}
                        </td>
                        <td className="px-12 py-10 text-neutral-700">
                          <div>{readText(line['code_combination_value'])}</div>
                          <div className="mt-2 text-xs text-neutral-500">
                            {readText(line['code_combination_desc'])}
                          </div>
                        </td>
                        <td className="px-12 py-10 text-right font-medium text-neutral-900">
                          {formatWholePKR(line['amount'])}
                        </td>
                        <td className="px-12 py-10 text-right text-neutral-700">
                          {formatWholePKR(line['taxable_amount'])}
                        </td>
                        <td className="px-12 py-10 text-right text-neutral-700">
                          {formatWholePKR(line['tax_amount'])}
                        </td>
                        <td className="px-12 py-10 text-neutral-700">
                          {readTaxStatus(line['stax_checked'])}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </section>
    </BucketShell>
  );
}

function readText(value: unknown): string {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
}

function readTaxStatus(value: unknown): string {
  return value === true ? 'Checked' : value === false ? 'Unchecked' : '-';
}

function formatWholePKR(value: unknown): string {
  if (value === null || value === undefined || value === '') return '-';
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return formatPKR(Math.ceil(n));
}

function readPaymentTerm(preflight: Record<string, unknown>): string {
  const name = readText(preflight['payment_term_name']);
  const id = readText(preflight['payment_term_id']);
  if (name !== '-' && id !== '-') return `${name} (${id})`;
  return name !== '-' ? name : id;
}

function Readout({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-neutral-200 bg-neutral-50 p-12">
      <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">{label}</p>
      <p className="mt-4 truncate text-sm font-medium text-neutral-900" title={value}>
        {value || '-'}
      </p>
    </div>
  );
}
